import { IBook, IBookTable } from './IBookTable';
import { IBookContent, IChapterContentAndAnswers, ILessonPreview } from './IBookContent';
import { IChannelCreate, IChannelJsonSave, IProgram } from './IChannel';
import { ICourse, IGroupUpdateInfo, IGroupUser, IStudent, ITrainingDetails } from './IClassManagement';
import { SERVER_URL, getAuthStr, getJwtObj, getToken, isUserSignedIn, wipeSignInState } from '../SharedCommon/utils';
import { getOrgClassProgressionControl, getOrgLogo, getOrgServer } from '../SharedCommon/OrgList';

import BookTable from '../sample/empty-free.json';
import { IAttendanceRecord } from '../components/GroupMemberTool';
import { IChatMessage } from './IChatData';
import { _bibleData } from './BibleData';
import createAxiosInstance from './customAxios';
import i18n from '../i18n';
import { getRuntimeVariant, Variant } from '../SharedCommon/variant';

export type IUserId = number;

// 发送消息的结果类型
export type SendMessageResult = number | 'SUCCESS_NO_ID' | 'FAILED';

export interface IUser {
  userId: IUserId;
  displayName: string;
  name?: string;
  loginId: string;
  uniqueId?: string;
  isGroupLeader: boolean;
  tag?: string;
  block?: boolean;
  id?: number;
  accepted?: boolean;
}

export interface IMessage {
  id: number;
  text: string;
  createdAt: Date;
  user: {
    id: number;
    name: string;
    avatar: string;
  };
  image?: string;
}

const isFishMeetVariant = getRuntimeVariant() === Variant.fishmeet;

export class DataProvider {
  private serverUrl = '';
  private bibleUrl = '';
  private refresh = true;
  private classes: any[] = [];
  private orgId?: number;
  private orgServerUrl?: string;
  private axiosInstance?: any; // = createAxiosInstance(this.lang);
  private myProfile?: any;
  private myClasses: number[] = [];
  private myTagsForUsers = {} as any;
  private myBlocks = {} as any;
  private displayNameChangeListeners: Array<() => void> = [];
  private coverVersion = 0;

  public init(apiurl: string, orgId?: number) {
    this.serverUrl = apiurl;
    this.orgId = orgId;
    this.orgServerUrl = getOrgServer(orgId ?? 0);

    // bible is part of the webapp build - use PUBLIC_URL to handle subpath deployments
    this.bibleUrl = `${process.env.PUBLIC_URL}/bible/`;
    this.axiosInstance = createAxiosInstance(this.serverUrl, () => i18n.language);

    const coverVersion = parseInt(localStorage.getItem('coverVersion') ?? '0');
    this.coverVersion = isNaN(coverVersion) ? 0 : coverVersion;
  }

  constructor(url: string, orgId?: number) {
    this.init(url, orgId);
  }

  public getServerUrl() {
    return this.serverUrl;
  }

  public async getMyProfile() {
    console.log('profile: ', this.myProfile);
    const jwt = getJwtObj();
    if (!this.myProfile && jwt.userId !== -1) {
      try {
        const response = await this.axiosInstance.get('/user/profile');
        this.myProfile = response.data;
      } catch (error) {
        console.error('Error fetching display name:', error);
      }
    }
    return this.myProfile ?? { displayName: jwt?.displayName ?? '', uniqueId: '' };
  }

  public reset() {
    this.myProfile = undefined;
    this.refresh = true;
    this.classes = []; //: any;

    wipeSignInState();
  }

  public getOrgId(): number {
    return this.orgId ? this.orgId : 0;
  }
  public getLogo() {
    return getOrgLogo(this.getOrgId());
  }
  public classProgressionControl(): boolean {
    return getOrgClassProgressionControl(this.getOrgId());
  }
  public moreUserDataForOrg(): boolean {
    return !!this.orgServerUrl;
  }

  public addDisplayNameChangeListener(listener: () => void) {
    this.displayNameChangeListeners.push(listener);
  }

  public removeDisplayNameChangeListener(listener: () => void) {
    this.displayNameChangeListeners = this.displayNameChangeListeners.filter((l) => l !== listener);
  }

  private notifyDisplayNameChange() {
    this.displayNameChangeListeners.forEach((listener) => listener());
  }

  public getAvatarSync(userId: number) {
    return `${SERVER_URL}/user/avatar/${userId}`;
  }

  public getBookCover(classId: number | string): string {
    return `${SERVER_URL}/lesson/${classId}/cover?t=${Math.floor(Date.now() / 1000 / 60 / 60 / 24)}&v=${
      this.coverVersion
    }`;
  }

  public async updateUserDisplayName(displayName: string): Promise<boolean> {
    try {
      await this.axiosInstance.put('/user/profile', { displayName });
      this.myProfile = { ...this.myProfile, displayName };
      this.notifyDisplayNameChange();
      return true;
    } catch (error) {
      console.error('Update display name error:', error);
      return false;
    }
  }

  public async uploadAvatar(file: File): Promise<string> {
    try {
      const formData = new FormData();
      formData.append('userAvatar', file);

      const response = await this.axiosInstance.put('/user/avatar', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.status === 201) {
        return response.data.avatarUrl;
      }
      throw new Error('Failed to upload avatar');
    } catch (error) {
      console.error('Upload avatar error:', error);
      throw error;
    }
  }

  public async getBible(bible: string): Promise<any> {
    try {
      const remoteUri = this.bibleUrl + bible + '.json';

      const cache = 'caches' in window ? await window.caches.open('bible') : undefined;
      if (cache) {
        const cachedResponse = await cache.match(remoteUri);
        if (cachedResponse) {
          const bibleJson = await cachedResponse.json();
          return bibleJson;
        }
      }

      const url = remoteUri;

      const response = await fetch(url);
      if (response.status === 200) {
        if (cache) {
          cache.put(remoteUri, response.clone());
        }
        const bibleJson = await response.json();
        return bibleJson;
      }
    } catch (error) {
      alert(error);
    }

    return undefined;
  }

  public async recordBibleUsage(bookId: number, chapter: number, verses: string): Promise<void> {
    try {
      await this.axiosInstance.post('/bible/usage', { translation: 'niv', bookId, chapter, verses });
    } catch (error) {
      // Usage logging is non-critical; swallow errors silently
    }
  }

  public async fetchJsonAttachment(url: string, signal?: AbortSignal): Promise<any> {
    const response = await this.axiosInstance.get(url, { signal });
    return response.data;
  }

  public async refreshAccessTokenIfNeededAsync(): Promise<boolean> {
    const jwt = getJwtObj();
    if (!jwt || typeof jwt.exp !== 'number') {
      return false;
    }

    //const expDate = new Date(jwt.exp * 1000);
    const daysDiff = Math.floor((jwt.exp * 1000 - Date.now()) / 1000 / 60 / 60 / 24);

    // refresh token when it's going to expire in 30 days
    console.log('Refresh?: ', daysDiff);
    if (daysDiff >= 30) {
      return false;
    }

    try {
      const response = await this.axiosInstance.post('/user/refreshAccessToken', {});
      localStorage.setItem('access_token', response.data.accessToken);
      return true;
    } catch (error) {
      console.log(error);
    }
    return false;
  }

  public async getBooks2(refresh: boolean): Promise<IBook[]> {
    try {
      if (!refresh) {
        if (!this.refresh && this.classes && this.myProfile) {
          console.log('getBooks2:return cached classes');
          return this.classes;
        }
      }

      let response;
      if (!getToken()) {
        response = await this.axiosInstance.get('/home');
        if (!this.orgId) {
          return response.data.books;
        }
        const books = response.data.books.filter((book: { orgId: number }) => book.orgId === this.orgId);
        this.classes = books;
        console.log('getBooks2 home: ', books);
        return books;
      } else {
        if (this.myProfile == undefined) {
          // call this expensive version only once
          response = await this.axiosInstance.get('/user/getUpdate');
          this.myClasses = response.data.myClasses ?? [];
          this.myProfile = response.data.user ?? {};
        } else {
          response = await this.axiosInstance.get('/user/classes');
        }
        let classes = response.data.classes as IBook[];
        classes = classes.filter((cls) => cls.status !== -1);

        if (this.orgId) {
          classes = classes.filter((cls) => cls.orgId === this.orgId);
        }

        classes.forEach((it) => {
          it.isMyClass = this.myClasses.indexOf(it.classId) !== -1;
        });

        this.refresh = false;
        this.classes = classes;
        console.log('getBooks2: ', classes);
        return classes;
      }
    } catch (error) {
      console.log(error);
    }

    //if (process.env.REACT_APP_testlocal !== '1' && !window.location.href.startsWith('http://localhost'))
    if (!window.location.href.startsWith('http://localhost')) {
      return [];
    }

    // retain the testing sample data --- while in development.
    return [
      {
        image: 'http://13.91.97.63/idigest/lesson/parenting/cover',
        name: 'resourceID1',
        classId: 1,
        classNameChs: '子女心，父母情',
        classNameCht: '子女心，父母情',
        classNameEng: '子女心，父母情'
      },
      {
        image: 'https://idigest.gtinternational.org/api/lesson/GodsLove/cover',
        name: 'resourceID2',
        classId: 2,
        classNameChs: 'Gods Love',
        classNameCht: 'Gods Love',
        classNameEng: 'Gods Love'
      },
      {
        image: 'http://13.91.97.63/idigest/lesson/FollowMe/cover',
        name: 'resourceID3',
        classId: 3,
        classNameChs: 'Follow me',
        classNameCht: 'Follow me',
        classNameEng: 'Follow me'
      }
    ];
  }

  public async getBookTable(lessonId: string): Promise<IBookTable> {
    if (lessonId === '917') {
      return _bibleData.getBookTable();
    }
    const url = `/lesson/${encodeURIComponent(lessonId)}`;
    try {
      const response = await this.axiosInstance.get(url);
      response.data.lessonId = lessonId;
      return response.data;
    } catch (error) {
      console.log('getBookTable: ', error);
      if (error.response?.status === 404) {
        BookTable.sessions[0].title = i18n.t('authoring.Chapter1');
        return {
          title: i18n.t('authoring.NoName'), //"我的新课程1",
          lessonId,
          ...JSON.parse(JSON.stringify(BookTable)),
          progress: []
        };
        //return { title: '404', lessonId, description: '', sessions: [], progress: [] };
        // an empty object for non-existent free.json
        // -- this is okay state when book is first created.
        //the recipient should deal with it.
      }
    }

    //BookTable.lessonId = "1";
    //title 550 --- bad failure!!!
    return {
      title: '550',
      lessonId,
      ...JSON.parse(JSON.stringify(BookTable)),
      progress: []
    };
  }

  public async getChapterContentAndAnswers(
    lessonId: string,
    chapterId: string,
    chapterIndex: number
  ): Promise<IChapterContentAndAnswers> {
    if (lessonId === '917') {
      return { lesson: await _bibleData.getBookContent(chapterId) };
    }

    //try to refresh token with each chapter, assuming the user
    //only use the tool once a week to study a chapter, this would be
    //weekly checking to see if there needs to be refreshed.
    //checking more often is cheap...
    await this.refreshAccessTokenIfNeededAsync();

    const groupId = 0;

    //the groupID isn't getting used in the service API.
    //the access check is based on *all* the groups a user belongs to, and that
    //is checked against DB directly. so: TODO: what is the gorup ID for??
    //this.sessionIndex is the week, it's the index into the tableofcontent as each chapter is organized as if it's weekly
    //this.lessonFile is the json file name for that chapter ===
    const emptyLesson = {
      title: '',
      version: 0,
      sessions: [{ title: '', content: [] }],
      lessonId,
      chapterId
    };

    const url = `/lesson/${encodeURIComponent(lessonId)}/${groupId}/${chapterId}?index=${chapterIndex}`;
    try {
      const response = await this.axiosInstance.get(url);
      console.log(response.data);
      response.data.lesson.lessonId = lessonId;
      return response.data;
    } catch (error) {
      console.log('getChapterContentAndAnswers', error);
      emptyLesson.title = '$Error, Try again:' + error.response?.status;
      if (error.response?.status === 404) {
        emptyLesson.title = '404';
        return { lesson: emptyLesson };
      }
    }

    return { lesson: emptyLesson };
  }

  public async saveUserData(dataId: string, body: any): Promise<boolean> {
    //  const result = await callWebServiceAsync(`${Models.HostHttpServer}user/data/answer,${dataId}`, 'PUT', body);
    //async saveAsync(dataId, questionId, answerText) {
    //const body = this.answers;
    //body[questionId] = answerText;
    console.log('saveUserData: ', body);
    const url = `/user/data/${dataId}`;
    try {
      await this.axiosInstance.put(url, body);
      return true;
    } catch (error) {
      console.log(error);
    }

    return false;
  }

  public async getUserGroups(): Promise<any> {
    const url = '/groups';
    try {
      const response = await this.axiosInstance.get(url);
      return response.data;
    } catch (error) {
      console.log(error);
    }
  }

  async getAllContactAsync() {
    const url = '/user/contact';
    try {
      const response = await this.axiosInstance.get(url);
      return response.data;
    } catch (error) {
      console.log(error);
    }
    return {};
  }

  public async updateContactForUser(userId: number, tag: string, block: number) {
    const url = `/user/contact/${userId}`;
    try {
      await this.axiosInstance.put(url, { tag, block });
      this.myTagsForUsers[userId] = tag;
      this.myBlocks[userId] = block;
      return true;
    } catch (error) {
      console.log(error);
    }
    return false;
  }

  public getDisplayNameForUser(user: any) {
    return `${user.displayName} ${this.myTagsForUsers[user.userId || user.id] ?? ''} (${user.loginId})`;
  }

  public getMyBlockForUser(userId: number) {
    return !!this.myBlocks[userId];
  }

  public getMyTagForUser(userId: number) {
    return this.myTagsForUsers[userId] ?? '';
  }

  public async getUserContacts(filterBlocked = true): Promise<any> {
    const url = '/user/inviteUsers';
    try {
      const response = await this.axiosInstance.get(url);
      let inviteUsers = response.data;
      if (!filterBlocked) {
        return inviteUsers;
      }
      const contactInfo = await this.getAllContactAsync();
      const blockedUsers = new Set([
        ...contactInfo.me.filter((u: any) => u.block).map((u: any) => u.userId),
        ...contactInfo.blockedBy
      ]);

      this.myBlocks = contactInfo.me.reduce((blocks: any, contact: any) => {
        blocks[contact.userId] = contact.block !== 0;
        return blocks;
      }, {});

      this.myTagsForUsers = contactInfo.me.reduce((tags: any, contact: any) => {
        tags[contact.userId] = contact.tag;
        return tags;
      }, {});

      inviteUsers = inviteUsers.filter((it: any) => !blockedUsers.has(it.userId));
      inviteUsers.forEach((it: any) => (it.name = this.getDisplayNameForUser(it)));
      return inviteUsers;
    } catch (error) {
      console.log(error);
    }
    return [];
  }

  public async inviteUser(email: string, greetings: string): Promise<any> {
    const url = '/user/invite';
    const data = {
      email: email,
      greetings: greetings
    };

    try {
      const response = await this.axiosInstance.post(url, data);
      return response.data;
    } catch (error) {
      console.log(error);
      return null;
    }
  }

  public async getCurrentLoggedInUser(): Promise<any> {
    try {
      const response = await this.axiosInstance.get('/user/profile');
      return response.data;
    } catch (error) {
      console.log(error);
    }
  }

  public async createSmallGroup(groupName: string, members: string[]): Promise<void> {
    const url = '/group/0/create';
    const payload = {
      groupName,
      members
    };

    try {
      const response = await this.axiosInstance.post(url, payload);
      console.log('Group created:', response.data);
    } catch (error) {
      console.error('Error creating group:', error);
      throw new Error('Failed to create group'); // Optionally re-throw to handle errors in the component
    }
  }

  public async createOneOnOneGroup(friendId: string): Promise<any> {
    const url = '/group/createOneOnOne';
    const payload = {
      friendId
    };

    try {
      const response = await this.axiosInstance.post(url, payload);
      return response.data;
    } catch (error) {
      console.error('Error creating group:', error);
      return null;
    }
  }

  //this logic seems off
  public async getUserGroupsAsLeader(): Promise<any> {
    const url = '/groups';
    const groupList = [];
    try {
      const response = await this.axiosInstance.get(url);
      for (let i = 0; i < response.data.length; i++) {
        if (response.data[i].isGroupLeader === 1 && response.data[i].isOrgGroup === 0) {
          groupList.push(response.data[i].name);
        }
      }
      return groupList;
    } catch (error) {
      console.log(error);
    }
  }

  public async getOrgGroup(): Promise<any> {
    const url = '/groups';
    const groupList = [];
    try {
      const response = await this.axiosInstance.get(url);
      for (let i = 0; i < response.data.length; i++) {
        if (response.data[i].isOrgGroup === 1 && response.data[i].isGroupLeader === 0) {
          groupList.push(response.data[i].name);
        }
      }
      return groupList;
    } catch (error) {
      console.log(error);
    }
  }

  public async getAllUsersForGroupLeader(check: boolean): Promise<any> {
    const url = '/groups';
    const groupList = [];
    try {
      const response = await this.axiosInstance.get(url);
      if (check === false) {
        for (let i = 0; i < response.data.length; i++) {
          if (response.data[i].isGroupLeader === 1) {
            const secondUrl = `/group/${response.data[i].groupId}`;
            const secondResponse = await this.axiosInstance.get(secondUrl);
            groupList.push(secondResponse.data.users.length);
          }
        }
      } else {
        for (let i = 0; i < response.data.length; i++) {
          if (response.data[i].isGroupLeader === 1 && response.data[i].isOrgGroup === 1) {
            const secondUrl = `/group/${response.data[i].groupId}`;
            const secondResponse = await this.axiosInstance.get(secondUrl);
            groupList.push(secondResponse.data.users.length);
          }
        }
      }
      return groupList;
    } catch (error) {
      console.log(error);
    }
  }

  public async getAllUsersForGroupMember(check: boolean): Promise<any> {
    const url = '/groups';
    const groupList = [];
    try {
      const response = await this.axiosInstance.get(url);
      if (check === false) {
        for (let i = 0; i < response.data.length; i++) {
          if (response.data[i].isGroupLeader === 0 && response.data[i].isOrgGroup === 0) {
            const secondUrl = `/group/${response.data[i].groupId}`;
            try {
              const secondResponse = await this.axiosInstance.get(secondUrl);
              groupList.push(secondResponse.data.users.length);
            } catch (e) {
              console.log(e);
            }
          }
        }
      } else {
        for (let i = 0; i < response.data.length; i++) {
          if (response.data[i].isGroupLeader === 0 && response.data[i].isOrgGroup === 1) {
            const secondUrl = `/group/${response.data[i].groupId}`;
            try {
              const secondResponse = await this.axiosInstance.get(secondUrl);
              groupList.push(secondResponse.data.users.length);
            } catch (e) {
              console.log(e);
            }
          }
        }
      }
      return groupList;
    } catch (error) {
      console.log(error);
    }
  }

  public async getSpecificGroupID(): Promise<any> {
    const url = '/groups';
    const groupList = [];
    try {
      const response = await this.axiosInstance.get(url);
      for (let i = 0; i < response.data.length; i++) {
        if (response.data[i].isGroupLeader === 1) {
          groupList.push(response.data[i].groupId);
        }
      }
      return groupList;
    } catch (error) {
      console.log(error);
    }
  }

  public async getOrgGroupList(orgId: number): Promise<any> {
    const url = `/webAdmin/${orgId}/groupList`;
    try {
      const response = await this.axiosInstance.get(url);
      return response.data;
    } catch (error) {
      console.log(error);
    }
  }

  public async getAdminGroupsAttendance(orgId: number, check: boolean): Promise<any> {
    const url = `/webAdmin/${orgId}/groupList`;
    const groupList = [];
    try {
      const response = await this.axiosInstance.get(url);
      if (check === false) {
        for (let i = 0; i < response.data.length; i++) {
          const GroupId = response.data[i].GroupId;
          const secondUrl = '/webAdmin/groups';
          try {
            const secondResponse = await this.axiosInstance.get(secondUrl);
            let flag = false;
            for (let j = 0; j < secondResponse.data.length; j++) {
              if (secondResponse.data[j].groupId === GroupId) {
                flag = true;
                groupList.push(secondResponse.data[j].users.length);
                break;
              }
            }
            if (flag === false) {
              groupList.push(0);
            }
          } catch (error) {
            console.log(error);
          }
        }
      }
      console.log(groupList);
      return groupList;
    } catch (error) {
      console.log(error);
    }
  }

  public async getUserGroupsAsMember(): Promise<any> {
    const url = '/groups';
    const groupList = [];
    try {
      const response = await this.axiosInstance.get(url);
      for (let i = 0; i < response.data.length; i++) {
        if (response.data[i].isGroupLeader === 0 && response.data[i].isOrgGroup === 0) {
          groupList.push(response.data[i].name);
        }
      }
      return groupList;
    } catch (error) {
      console.log(error);
    }
  }

  public async getUserData(dataId: string): Promise<string> {
    const url = `/user/data/${dataId}`;
    try {
      const response = await this.axiosInstance.get(url);
      return response.data.content;
    } catch (error) {
      console.log(error);
      if (error.response?.status === 404) {
        return 'Enter some notes here';
      }
    }

    return 'Data retrieval failed';
  }

  // get a json from server about student inputs
  public async getClassAnswers(orgId: number | undefined, trainingId: number | null, classId: number) {
    const url = `/organizations/${orgId}/trainings/${trainingId}/classes/${classId}/answers`;
    try {
      const response = await this.axiosInstance.get(url);
      console.log('here is the response json from class answers:', response.data);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch class answers:', error);
      throw error; // Optionally rethrow the error for the caller to handle
    }
  }

  public async getWebAdminUserList(orgId: number) {
    const url = `/webAdmin/${orgId}/userList`;
    try {
      const response = await this.axiosInstance.get(url);
      return response.data.length;
    } catch (error) {
      console.log(error);
    }
  }

  public async getAttendanceData(orgId: number): Promise<any> {
    try {
      const url = `/webAdmin/attendance/${orgId}`;
      const response = await this.axiosInstance.get(url);
      return response.data;
    } catch (error) {
      console.log(error);
    }
  }

  public async getSortedGroupsAttendance(orgId: number, flag: boolean): Promise<any> {
    const leaderList = [];
    const groupList = new Map();
    const returnLeaderList = [];
    const returnMemberList: any[] = [];
    try {
      const url = '/groups';
      const response = await this.axiosInstance.get(url);
      for (let i = 0; i < response.data.length; i++) {
        if (response.data[i].isGroupLeader === 1) {
          leaderList.push(response.data[i].groupId);
        }
      }
      try {
        const secondUrl = `/webAdmin/${orgId}/grouplist`;
        const attendance = await this.axiosInstance.get(secondUrl);
        for (let i = 0; i < attendance.data.length; i++) {
          groupList.set(attendance.data[i].GroupId, attendance.data[i].GroupName);
        }
        for (let i = 0; i < leaderList.length; i++) {
          if (groupList.has(leaderList[i])) {
            returnLeaderList.push(groupList.get(leaderList[i]));
            groupList.delete(groupList.get(leaderList[i]));
          }
        }
        groupList.forEach((item) => {
          returnMemberList.push(item);
        });
        if (flag) {
          return returnLeaderList;
        } else {
          return returnMemberList;
        }
      } catch (e) {
        console.log(e);
      }
    } catch (error) {
      console.log(error);
    }
  }

  public async getSpecificGroupAttendance(groupNumber: number): Promise<any> {
    try {
      const url = `/attendance/${groupNumber}`;
      const response = await this.axiosInstance.get(url);
      return response.data;
    } catch (error) {
      console.log(error);
    }
  }

  /// for book authors
  public async getOwnerBooks(): Promise<IBook[]> {
    try {
      let response;
      if (this.orgId) {
        response = await this.axiosInstance.get('/user/classes');
      } else {
        response = await this.axiosInstance.get('/bookAdmin/lessons');
      }
      const parseTags = (raw: string | string[] | undefined): string[] | undefined => {
        if (raw == null) {
          return undefined;
        }
        if (Array.isArray(raw)) {
          return raw.filter((t) => typeof t === 'string' && t.trim());
        }
        return typeof raw === 'string'
          ? raw
              .split(',')
              .map((t) => t.trim())
              .filter(Boolean)
          : undefined;
      };
      if (this.orgId) {
        return response.data.classes.map((cls: any) => ({
          name: cls.name,
          classId: cls.classId,
          classNameChs: cls.classNameChs,
          status: cls.status,
          classType: cls.classType,
          orgId: cls.orgId,
          allowDownload: cls.allowDownload,
          tags: parseTags(cls.tags)
        }));
      } else {
        const data = response.data;
        if (Array.isArray(data)) {
          return data.map((b: any) => ({
            ...b,
            tags: parseTags(b.tags) ?? b.tags
          }));
        }
        return response.data;
      }
    } catch (error) {
      console.log(error);
    }

    return [];
    /*
    // retain the testing sample data --- while in development.
    return [
      {
        name:'1',
        classId: 0,  // the classID in the database --
        classNameChs: '',
        classNameCht: '',
        classNameEng: '子女心，父母情',
        status: 0
      },
      {
        name:'2',
        classId: 1,  // the classID in the database --
        classNameChs: '',
        classNameCht: '',
        classNameEng: 'Example book2',
        status: 1,
        classType: 0
      },
      {
        name:'3',
        classId: 2,  // the classID in the database --
        classNameChs: '',
        classNameCht: '',
        classNameEng: 'Example book3',
        status: 1,
        classType: 1
      }
    ];
    */
  }

  public async createBook(bookName: string): Promise<IBook> {
    try {
      // post: {titleEng,titleCht,titleChs, image}
      // we need to get an image URL -- but for now -- it's missing
      let response;
      if (this.orgId) {
        response = await this.axiosInstance.post('/bookAdmin/lesson/create', {
          titleEng: bookName,
          organizationId: this.orgId
        });
      } else {
        response = await this.axiosInstance.post('/bookAdmin/lesson/create', { titleEng: bookName });
      }
      if (response.status === 200 || response.status === 201) {
        const newBook = {
          name: response.data.lessonId,
          classId: 0, //this is not return;
          classNameChs: response.data.titleChs,
          classNameCht: response.data.titleCht,
          classNameEng: response.data.titleEng,
          status: 0,
          classType: 0
        };
        //return response.data;
        this.refresh = true;
        return newBook;
      }
    } catch (error) {
      console.log(error);
    }

    return {
      classId: 0, // the classID in the database --
      name: '__fakeId',
      classNameChs: bookName,
      classNameCht: bookName,
      classNameEng: bookName,
      status: 0,
      classType: 0
    };
  }

  ///bookAdmin/lesson/:lessonId/update
  public async updateBookImage(lessonId: string, bookUrl: string): Promise<boolean> {
    const url = `/bookAdmin/lesson/${encodeURIComponent(lessonId)}/update`;
    try {
      await this.axiosInstance.post(url, { ImageUrl: bookUrl });
      return true;
    } catch (error) {
      console.log(error);
    }

    return false;
  }

  public async deleteBook(lessonId: string): Promise<boolean> {
    const url = `/bookAdmin/lesson/${encodeURIComponent(lessonId)}/update`;
    try {
      const book = {
        Status: -1
      };
      await this.axiosInstance.post(url, book);
      return true;
    } catch (error) {
      console.log(error);
    }

    return false;
  }

  ///bookAdmin/lesson/:lessonId/update — single endpoint for partial updates
  public async updateLesson(
    lessonId: string,
    updates: Partial<{
      ClassNameChs: string;
      Status: number;
      ClassType: number;
      allowDownload: number;
      tags: string[];
    }>
  ): Promise<boolean> {
    const url = `/bookAdmin/lesson/${encodeURIComponent(lessonId)}/update`;
    const payload: Record<string, number | string | string[]> = {};
    if (updates.ClassNameChs !== undefined) {
      payload.ClassNameChs = updates.ClassNameChs;
    }
    if (updates.Status !== undefined) {
      payload.Status = updates.Status;
    }
    if (updates.ClassType !== undefined) {
      payload.ClassType = updates.ClassType;
    }
    if (updates.allowDownload !== undefined) {
      payload.allowDownload = updates.allowDownload;
    }
    if (Array.isArray(updates.tags)) {
      payload.tags = updates.tags;
    }
    if (Object.keys(payload).length === 0) {
      return true;
    }
    try {
      const response = await this.axiosInstance.post(url, payload);
      console.log('updateLesson: ', payload, response);
      return true;
    } catch (error) {
      console.log(error);
      return false;
    }
  }

  public async updateBook(
    lessonId: string,
    Status: number,
    ClassType: number,
    allowDownload = 0,
    tags?: string[],
    ClassNameChs?: string
  ): Promise<boolean> {
    const updates: {
      Status: number;
      ClassType: number;
      allowDownload: number;
      tags?: string[];
      ClassNameChs?: string;
    } = {
      Status,
      ClassType,
      allowDownload
    };
    if (Array.isArray(tags)) {
      updates.tags = tags;
    }
    if (ClassNameChs !== undefined) {
      updates.ClassNameChs = ClassNameChs;
    }
    return this.updateLesson(lessonId, updates);
  }

  public async updateBookTitle(lessonId: string, title: string): Promise<boolean> {
    return this.updateLesson(lessonId, { ClassNameChs: title });
  }

  public async uploadFile(lessonId: string, file: any, callback: any, serverFilename?: string): Promise<boolean> {
    const url = `/bookAdmin/lesson/${encodeURIComponent(lessonId || '')}/uploadFile?newName=${encodeURIComponent(
      serverFilename || file?.name || Date.now()
    )}`;

    const formData = new FormData();
    formData.append('file', file);
    try {
      const response = await this.axiosInstance.post(url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent: any) => {
          const progress = progressEvent.loaded / progressEvent.total;
          if (callback) {
            callback(progress);
          }
        }
      });

      console.log('upload response: ', response);
      file!.newName = response.data.filename;
      if (serverFilename?.startsWith('cover.')) {
        this.coverVersion++;
        localStorage.setItem('coverVersion', this.coverVersion.toString());
      }
      return true;
    } catch (error) {
      console.log('upload file error: ', error);
    }

    return false;
  }

  public async saveContent(content: IBookTable | IBookContent, filename: string): Promise<boolean> {
    const url = `/bookAdmin/lesson/${encodeURIComponent(content.lessonId as string)}/save/${filename}`;
    try {
      //remove the properties we added, but not necessary, in the process of passing data around
      //// eslint-disable-line
      // eslint-disable-next-line
      const { lessonId = '', chapterId = '', bookTitle = '', ...ccc } = content;
      //@typescript-eslint/no-unused-vars

      await this.axiosInstance.post(url, ccc);
      return true;
    } catch (error) {
      console.log(error);
    }

    return false;
  }

  public getEmptyBook(lessonId: string): IBookTable {
    BookTable.sessions[0].title = i18n.t('authoring.Chapter1');
    return {
      title: i18n.t('authoring.NoName'), //"我的新课程1",
      lessonId,
      ...JSON.parse(JSON.stringify(BookTable))
    };
  }

  public async getContent(lessonId: string, filename: string): Promise<IBookTable | IBookContent> {
    if (lessonId === '917') {
      return await _bibleData.getBookContent(filename);
    }

    const url = `/bookAdmin/lesson/${encodeURIComponent(lessonId)}/content/${filename}`;
    try {
      const response = await this.axiosInstance.get(url);
      response.data.lessonId = lessonId;
      if (filename !== 'free.json') {
        response.data.chapterId = filename;
      }
      return response.data;
    } catch (error) {
      console.log('bookadmin/getContent', error, error.response);
      if (error.response?.status === 404) {
        //the book was first created or the chapter first created, so handle this gracefully
        if (filename === 'free.json') {
          return this.getEmptyBook(lessonId);
        } else {
          //return { title: '404', lessonId, description: '', sessions: [], progress: [] };
          // an empty object for non-existent free.json
          // -- this is okay state when book is first created.
          const emptyLesson = {
            title: '404',
            version: 0,
            sessions: [{ title: '', content: [] }],
            lessonId,
            chapterId: filename
          };
          return emptyLesson;
        }
      }
    }

    //BookTable.lessonId = "1";
    //title 550 --- bad failure!!!
    return {
      title: '550',
      lessonId,
      ...JSON.parse(JSON.stringify(BookTable)),
      progress: []
    };
  }

  public async getLessonPreview(lessonId: string, weekN: string): Promise<ILessonPreview> {
    const url = `/preview/${lessonId}/${weekN}`;
    try {
      const response = await this.axiosInstance.get(url);
      response.data.lessonId = lessonId;
      return response.data;
    } catch (error) {
      console.error(error);
    }

    return {
      title: '550',
      lessonId,
      ...JSON.parse(JSON.stringify(BookTable))
    };
  }

  public async getPreviewContent(lessonId: string, filename: string): Promise<IBookTable | IBookContent> {
    const url = `/lesson/${encodeURIComponent(lessonId)}/content/${filename}`;
    try {
      const response = await this.axiosInstance.get(url);
      response.data.lessonId = lessonId;
      if (filename !== 'free.json') {
        response.data.chapterId = filename;
      }
      return response.data;
    } catch (error) {
      console.error(error);
    }

    return {
      title: '550',
      lessonId,
      ...JSON.parse(JSON.stringify(BookTable))
    };
  }

  //
  // app.post('/group/createDiscussion', useAuth, createDiscussionAsync);
  // sample:
  // request payload: {classId: 334, week: 1, questionId: "C2_JOHN_2_5_1_0"}
  // response payload: {"studyGroupId":49158}
  //
  public async postCreateDiscussion(classId: number, week: number, questionId: string): Promise<number> {
    const url = '/group/createDiscussion';

    try {
      const response = await this.axiosInstance.post(url, { classId, week, questionId });
      return response.data.studyGroupId;
    } catch (error) {
      console.log(error);
    }

    return 0;
  }

  public async postGroupMessage(
    groupId: number,
    message: string,
    replyMessageId = -1,
    mentionedUsers?: any
  ): Promise<SendMessageResult> {
    const url = '/chat/sendMessage';
    try {
      const response = await this.axiosInstance.post(url, { room: groupId, message, replyMessageId, mentionedUsers });
      const messageId = this.extractMessageId(response.data);
      if (messageId) {
        return messageId;
      }
      if (response.data === '' || response.data === null || response.data === undefined) {
        // 后端返回 2xx 但响应体为空，消息会通过 WebSocket 推送
      }
      return 'SUCCESS_NO_ID';
    } catch (error) {
      console.error('postGroupMessage error:', error);
    }
    return 'FAILED'; // 请求失败
  }

  public async uploadFileWithChatMessage(
    file: any,
    groupId: number,
    message?: string,
    options?: { type?: string; duration?: number; isAnonymous?: boolean; shareInGroup?: number }
  ): Promise<boolean> {
    // Validate groupId
    const validGroupId = Number(groupId);
    if (isNaN(validGroupId) || !Number.isInteger(validGroupId) || validGroupId <= 0) {
      console.error('Invalid groupId:', groupId);
      return false;
    }

    // Validate and determine file type
    const ALLOWED_TYPES = ['image', 'file', 'audio'] as const;
    let type = options?.type;
    if (!type) {
      type = file.type?.indexOf('image') !== -1 ? 'image' : 'file';
    }
    if (!ALLOWED_TYPES.includes(type as (typeof ALLOWED_TYPES)[number])) {
      console.warn('Invalid type, defaulting to file:', type);
      type = 'file';
    }

    // Validate file size
    const validFileSize = Number(file.size);
    if (isNaN(validFileSize) || !Number.isInteger(validFileSize) || validFileSize < 0) {
      console.error('Invalid file size:', file.size);
      return false;
    }

    // Build request parameters
    const params = new URLSearchParams({
      groupId: String(validGroupId),
      type: type,
      fileSize: String(validFileSize),
      displayName: encodeURIComponent(file.name)
    });

    // Add optional parameters
    if (options?.duration !== undefined && options.duration !== null) {
      const duration = Number(options.duration);
      if (!isNaN(duration) && duration > 0 && duration <= 600000) {
        params.append('duration', String(Math.floor(duration)));
      } else {
        console.warn('Invalid duration value:', options.duration);
      }
    }

    if (options?.isAnonymous !== undefined && options.isAnonymous !== null) {
      params.append('isAnonymous', String(Boolean(options.isAnonymous)));
    }

    if (options?.shareInGroup !== undefined && options.shareInGroup !== null) {
      const shareInGroup = Number(options.shareInGroup);
      if (!isNaN(shareInGroup) && Number.isInteger(shareInGroup) && shareInGroup > 0) {
        params.append('shareInGroup', String(shareInGroup));
      } else {
        console.warn('Invalid shareInGroup value:', options.shareInGroup);
      }
    }

    // Upload file
    const formData = new FormData();
    formData.append('chatFile', file);
    const uploadUrl = `/chat/sendFileMessage?${params.toString()}`;

    try {
      const response = await this.axiosInstance.post(uploadUrl, formData, {
        headers: {
          Authorization: getAuthStr()
        }
      });

      if (response.status === 201) {
        // Optionally send a text message after file upload
        if (message) {
          const newMessageId = await this.postGroupMessage(groupId, message);
          // 使用明确的类型检查
          return newMessageId !== 'FAILED';
        }
        return true;
      }
    } catch (error) {
      console.error('uploadFileWithChatMessage error:', error);
    }

    return false;
  }

  public async uploadFile2Chat(file: any): Promise<string> {
    const formData = new FormData();
    formData.append('chatFile', file);
    const uploadUrl = '/chat/uploadFile';

    try {
      const response = await this.axiosInstance.post(uploadUrl, formData, {
        headers: {
          Authorization: getAuthStr()
        }
      });
      return response.data.filename || '';
    } catch (error) {
      console.error('uploadFile2Chat error:', error);
    }

    return '';
  }

  public async getGroupMessage(groupId: number, queryString = ''): Promise<any> {
    const url = `/chat/getMessages/${groupId}${queryString ? `?${queryString}` : ''}`;
    try {
      const response = await this.axiosInstance.get(url);
      return response.data.messages || [];
    } catch (error) {
      console.error('getGroupMessage error:', error);
    }
    return [];
  }

  async getFileListInGroup(groupId: number, dayRange = 36): Promise<IChatMessage[] | null> {
    try {
      const response = await this.axiosInstance.get(`/chat/${groupId}/files?fromDays=${dayRange}`);
      const files = response.data as Array<IChatMessage>;
      return files.sort((a, b) => b.time - a.time);
    } catch (error: any) {
      console.error('getFileListInGroup error:', error);
      return null;
    }
  }

  getFileUrlInChat(groupId: number, fileId: number): string {
    return `${this.serverUrl}/chat/${groupId}/files/${fileId}`;
  }

  public async batchShareMessages(
    groupId: number,
    targetGroupId: number,
    messageIds: number[],
    mode: 'individual' | 'merged'
  ): Promise<number[] | null> {
    const url = `/chat/${groupId}/forward/${targetGroupId}?mode=${mode}`;
    try {
      const response = await this.axiosInstance.post(url, { messageIds });
      if (response.status === 200 || response.status === 201) {
        const newMessageIds = this.extractMessageIds(response.data);
        if (newMessageIds.length > 0) {
          return newMessageIds;
        }
        console.error(
          `[Chat] Backend did not return message_ids in batchShareMessages response for group ${groupId} -> ${targetGroupId}`,
          { responseData: response.data, status: response.status }
        );
      }
    } catch (error: any) {
      console.error('batchShareMessages error:', error);
      if (error.response) {
        console.error('Response status:', error.response.status);
        console.error('Response data:', error.response.data);
      }
    }
    return null;
  }

  public async deleteChatMessage(chatId: number, messageId: number): Promise<boolean> {
    const url = `/chat/${chatId}/${messageId}`;
    try {
      await this.axiosInstance.delete(url);
      return true;
    } catch (error) {
      console.error('deleteChatMessage error:', error);
    }
    return false;
  }

  public async batchDeleteMessages(groupId: number, messageIds: number[]): Promise<boolean> {
    if (messageIds.length === 0 || messageIds.length > 20) {
      console.error('Message IDs array must contain 1-20 items');
      return false;
    }

    const idsParam = messageIds.join(',');
    const url = `/chat/${groupId}/messages?ids=${idsParam}`;
    try {
      await this.axiosInstance.delete(url);
      return true;
    } catch (error: any) {
      console.error('batchDeleteMessages error:', error);
      if (error.response) {
        console.error('Response status:', error.response.status);
        console.error('Response data:', error.response.data);
      }
    }
    return false;
  }

  public async getTaggedMessages(chatId: number, category: 'group' | 'user'): Promise<any> {
    const url = `/user/collection/${chatId}/${category}`;
    try {
      const response = await this.axiosInstance.get(url);
      return response.data.messages || [];
    } catch (error) {
      console.error('getTaggedMessages error:', error);
      return null;
    }
  }

  public async updateCollectionMessage(
    groupId: number,
    source: 'group' | 'user',
    data: { message: string; user: number; time: number; messageId?: number | string },
    silent = false
  ): Promise<boolean> {
    const url = `/user/collection/${groupId}/${source}`;
    try {
      const response = await this.axiosInstance.post(url, data);
      if (response.status === 200 || response.status === 201) {
        return true;
      }
    } catch (error: any) {
      const status = error?.response?.status || error?.status || error?.code;
      if (silent && (status === 400 || status === 'ERR_BAD_REQUEST')) {
        return false;
      }
      if (!silent) {
        console.error('updateCollectionMessage error:', error);
      }
    }
    return false;
  }

  public async batchUpdateCollectionMessage(
    groupId: number,
    target: 'group' | 'user',
    data: Array<{ attachment?: any; message: string; user: number; time: number; messageId?: number | string }>
  ): Promise<{ success: boolean; successCount?: number; failCount?: number; failedMessageIds?: (number | string)[] }> {
    const url = `/user/collection/${groupId}/${target}`;
    const defaultFailure = { success: false, failCount: data.length, successCount: 0 };

    try {
      const response = await this.axiosInstance.post(url, { data });

      if (response.status >= 200 && response.status < 300) {
        const responseData = response.data;

        if (responseData && typeof responseData === 'object') {
          const successCount = typeof responseData.successCount === 'number' ? responseData.successCount : undefined;
          const failCount = typeof responseData.failCount === 'number' ? responseData.failCount : undefined;

          if (typeof responseData.success === 'boolean') {
            return {
              success: responseData.success,
              successCount: successCount !== undefined ? successCount : responseData.success ? data.length : 0,
              failCount: failCount !== undefined ? failCount : responseData.success ? 0 : data.length,
              failedMessageIds: Array.isArray(responseData.failedMessageIds) ? responseData.failedMessageIds : []
            };
          }

          if (successCount !== undefined || failCount !== undefined) {
            let finalSuccessCount: number;
            let finalFailCount: number;
            let isSuccess: boolean;

            if (successCount !== undefined && failCount !== undefined) {
              finalSuccessCount = successCount;
              finalFailCount = failCount;
              isSuccess = failCount === 0 || successCount === data.length;
            } else if (successCount !== undefined) {
              finalSuccessCount = successCount;
              finalFailCount = data.length - successCount;
              isSuccess = successCount === data.length;
            } else {
              finalFailCount = failCount!;
              finalSuccessCount = data.length - failCount;
              isSuccess = failCount === 0;
            }

            return {
              success: isSuccess,
              successCount: finalSuccessCount,
              failCount: finalFailCount,
              failedMessageIds: Array.isArray(responseData.failedMessageIds) ? responseData.failedMessageIds : []
            };
          }
        }

        if (response.status === 200 || response.status === 201) {
          return {
            success: true,
            successCount: data.length,
            failCount: 0,
            failedMessageIds: []
          };
        }

        return defaultFailure;
      }

      throw new Error(`HTTP ${response.status}`);
    } catch (error: any) {
      return defaultFailure;
    }
  }

  public async batchRemoveCollectionMessages(
    groupId: number,
    target: 'user' | 'group',
    messageIds: number[]
  ): Promise<boolean> {
    if (!messageIds || messageIds.length === 0) {
      return false;
    }

    const url = `/user/collection/${groupId}/${target}`;
    try {
      await this.axiosInstance.delete(url, {
        data: { messageIds }
      });
      return true;
    } catch (error) {
      console.error('batchRemoveCollectionMessages error:', error);
    }
    return false;
  }

  public async removeCollectionSource(groupId: number, source: string, messageId: number): Promise<boolean> {
    const url = `/user/collection/${groupId}/${source}/${messageId}`;
    try {
      await this.axiosInstance.delete(url);
      return true;
    } catch (error) {
      console.error('removeCollectionSource error:', error);
    }
    return false;
  }

  private extractMessageId(responseData: any): number | null {
    if (!responseData || responseData === '') {
      return null;
    }

    let data = responseData;
    if (typeof responseData === 'string' && responseData.trim() !== '') {
      try {
        data = JSON.parse(responseData);
      } catch (e) {
        return null;
      }
    }

    if (typeof data !== 'object' || data === null) {
      return null;
    }

    const possibleIds = [
      data.messageId,
      data.id,
      data.message_id,
      data.data?.messageId,
      data.data?.id,
      data.result?.messageId,
      data.result?.id,
      // 尝试更多可能的字段路径
      data.response?.messageId,
      data.response?.id,
      data.body?.messageId,
      data.body?.id,
      data.content?.messageId,
      data.content?.id
    ];

    for (const id of possibleIds) {
      if (id !== undefined && id !== null) {
        const parsedId = typeof id === 'string' ? parseInt(id, 10) : id;
        if (!isNaN(parsedId) && parsedId > 0) {
          return parsedId;
        }
      }
    }

    // 如果无法提取 messageId，记录调试信息（只在开发环境或详细日志时输出）
    // 避免过多日志输出
    return null;
  }

  private extractMessageIds(responseData: any): number[] {
    if (!responseData) {
      return [];
    }

    if (Array.isArray(responseData)) {
      return responseData
        .map((id) => (typeof id === 'string' ? parseInt(id, 10) : id))
        .filter((id) => !isNaN(id) && id > 0);
    }

    if (typeof responseData === 'object') {
      const possibleArrays =
        responseData.messageIds ||
        responseData.message_id ||
        responseData.message_ids ||
        responseData.ids ||
        responseData.result ||
        responseData.data;

      if (Array.isArray(possibleArrays)) {
        return possibleArrays
          .map((id: any) => (typeof id === 'string' ? parseInt(id, 10) : id))
          .filter((id: any) => !isNaN(id) && id > 0);
      }
    }

    return [];
  }

  public async getManagedOrgsAndGroups(): Promise<any> {
    const url = '/organizations';
    try {
      const response = await this.axiosInstance.get(url);
      return response.data;
    } catch (error) {
      console.log(error);
    }

    return null;
  }

  public async postFeedback(message: string): Promise<boolean> {
    try {
      await this.axiosInstance.post('/feedback', { message });
      return true;
    } catch (error) {
      console.log(error);
    }

    return false;
  }

  public async getGroupInfo(id: number): Promise<any> {
    try {
      const response = await this.axiosInstance.get(`/group/${id}`);
      return response.data;
    } catch (error) {
      console.log(error);
    }

    return null;
  }

  /*
      const result = await callWebServiceAsync(getHttpsServer(`group/${groupId}/meetingLink`));
      const succeed = await showWebServiceCallErrorsAsync(result, 200);
      if (succeed) {
  */
  public async getGroupMeetLink(id: number): Promise<any> {
    try {
      const response = await this.axiosInstance.get(`/group/${id}/meetingLink`);
      return response.data;
    } catch (error) {
      console.log(error);
    }

    return null;
  }

  async createGroup(groupName: string) {
    try {
      const response = await this.axiosInstance.post('/group/0/create', { groupName });
      return response.data;
    } catch (error) {
      console.error('创建新组失败:', error);
      return null;
    }
  }

  async updateGroup(groupId: number, body: any) {
    const url = `/group/${groupId}`;
    try {
      await this.axiosInstance.put(url, body);
      return true;
    } catch (error) {
      console.error('更新组失败:', error);
    }
    return false;
  }

  async respondToRequest(groupId: number, requestUserId: number, accept: boolean) {
    const url = `/group/${groupId}/${accept ? 'accept' : 'reject'}`;
    try {
      const response = await this.axiosInstance.post(url, { requestUserId });
      console.log('response for respondToRequest:', response);
      return true;
    } catch (error) {
      console.error('Failed respondToRequest:', error);
    }
    return false;
  }

  // get the study progress for all users from a group for a class
  async getGroupStudyProgress(groupId: number, classId: number) {
    console.log(`Fetching study progress from /group/${groupId}/${classId}/studyProgress`);
    try {
      const response = await this.axiosInstance.get(`/group/${groupId}/${classId}/studyProgress`);
      console.log('response for study progress:', response);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch study progress:', error);
      return null;
    }
  }

  // for setting attendance data
  async setAttendance(attendanceRecord: IAttendanceRecord, groupId: number): Promise<boolean> {
    console.log(`Setting attendance for group ${groupId} on ${attendanceRecord}`);

    try {
      const userIds = attendanceRecord.users.map((user) => user.userId); // Extract user IDs from users array
      const allUserIds = attendanceRecord.allUsers.map((user) => user.userId); // Extract user IDs from allUsers array
      console.log('attended users:', userIds);
      console.log('all users:', allUserIds);
      if (!Array.isArray(userIds) || !Array.isArray(allUserIds)) {
        console.error('not arrays in body');
      }
      const dateString = attendanceRecord.date.split('T')[0].replace(/-(0)(\d)/g, '-$2');
      const response = await this.axiosInstance.put(`/attendance/${groupId}/${dateString}`, {
        users: userIds,
        allUsers: allUserIds
      });

      console.log('Response for setting attendance:', response);
      return true;
    } catch (error) {
      console.error('Error setting attendance:', error);
      return false;
    }
  }

  // update group info accordingly
  // group name has to be there, rest can be null
  async updateGroupInfo(groupUpdateInfo: IGroupUpdateInfo) {
    const { groupId, name, notice, meetingPassword, disappearingMessages, meetingData } = groupUpdateInfo;
    console.log(`Updating group info at /group/${groupId}/update`);

    try {
      const response = await this.axiosInstance.post(`/group/${groupId}/update`, {
        name,
        notice,
        meetingPassword,
        disappearingMessages,
        meetingData
      });

      console.log('response for update group info:', response);
      return 'success';
    } catch (error) {
      console.error('Failed to update group info:', error);
      return 'fail';
    }
  }

  // remove student from a class
  // takes groupId and studentId
  async deleteFromGroup(groupId: number, studentId: number) {
    try {
      const response = await this.axiosInstance.post(`/group/${groupId}/removeUser/${studentId}`, {});
      console.log('response for delete:', response);
      return 'success';
    } catch (error) {
      console.error('Failed to remove student:', error);
      return null;
    }
  }

  // promotes a member to leader, only works if you are the leader
  async promoteMember(groupId: number, memberUserId: number) {
    try {
      const response = await this.axiosInstance.post(`/group/${groupId}/${memberUserId}/promote`, {});
      console.log('Response for promote:', response);
      return 'success';
    } catch (error) {
      console.error('Failed to promote member:', error);
      return null;
    }
  }

  // demotes an admin
  async demoteMember(groupId: number, leaderUserId: number) {
    try {
      const response = await this.axiosInstance.post(`/group/${groupId}/${leaderUserId}/demote`, {});
      console.log('Response for demote:', response);
      return 'success';
    } catch (error) {
      console.error('Failed to demote member:', error);
      return null;
    }
  }

  // add student to group
  // uses groupid and studentlogin instead of studentid
  async addUserToGroup(groupId: number, studentLogin: string) {
    try {
      const response = await this.axiosInstance.post(`/group/${groupId}/addUser/${studentLogin}`, {});
      console.log('response for adding user:', response);
      return 'success';
    } catch (error) {
      console.error('Failed to add student:', error);
      return null;
    }
  }

  // delete group
  // takes groupId
  async deleteGroup(groupId: number) {
    try {
      const response = await this.axiosInstance.post('/group/delete', { groupId });
      console.log('response for delete group:', response);
      return 'success';
    } catch (error) {
      console.error('Failed to delete group:', error);
      return null;
    }
  }
  // delete groups (batch delete)
  async deleteGroups(groupIds: number[]): Promise<{ success: number[]; failed: { groupId: number; error: string }[] }> {
    try {
      const response = await this.axiosInstance.delete('/groups', {
        data: { groupIds }
      });
      return {
        success: response?.data?.deleted || [],
        // 去掉小组不存在的错误提示
        failed:
          response?.data?.errors && Array.isArray(response?.data?.errors)
            ? response.data.errors.filter((item: any) => item.error !== 'Invalid group')
            : []
      };
    } catch (error) {
      console.error('Failed to delete groups:', error);
      return { success: [], failed: groupIds.map((id) => ({ groupId: id, error: 'Network error' })) };
    }
  }

  // Classmanagement and Classregistration api:
  async handleCreateNewTraining(trainingDetails: ITrainingDetails) {
    try {
      const responseTraining = await this.axiosInstance.post(`/organizations/${this.orgId}/training`, trainingDetails);
      const responseGroup = await this.createGroup(trainingDetails.name + '学习小组');
      if (responseGroup) {
        await this.handleAddGroup2Training(responseGroup.response.studyGroupId, responseTraining.data.trainingId);
      }

      return responseTraining.data;
    } catch (error) {
      console.error('创建新课程失败:', error);
      return null;
    }
  }

  async handleAddGroup2Training(groupId: number, trainingId: number) {
    try {
      await this.axiosInstance.post(`/organizations/${this.orgId}/trainings/${trainingId}/groups/${groupId}`, {});
    } catch (error) {
      console.error('添加学习小组失败:', error);
    }
  }

  async fetchTrainings() {
    try {
      console.log('orgid = : ', this.orgId);
      const response = await this.axiosInstance.get(`/organizations/${this.orgId}/trainings`);
      return response.data;
    } catch (error) {
      console.error('获取课程列表失败:', error);
      return [];
    }
  }

  async fetchTrainingDetail(trainingId: number) {
    try {
      const response = await this.axiosInstance.get(`/organizations/${this.orgId}/trainings/${trainingId}`);
      console.log('获取课程详细信息: ', response.data);
      return response;
    } catch (error) {
      console.error('获取课程详细信息失败:', error);
      return { groups: [], classes: [], orgClasses: [], status: [] };
    }
  }

  async fetchCourses(): Promise<ICourse[] | undefined> {
    try {
      const response = await this.axiosInstance.get('/user/classes');
      console.log('user/classes: courses: ', response);
      return response.data.classes
        .filter((it: any) => it.orgId === this.orgId && it.status !== -1 && it.status !== 0)
        .map((course: any) => {
          return {
            ClassId: course.name,
            ClassNameChs: course.classNameChs
          };
        });
    } catch (error) {
      console.error('获取课程信息失败:', error);
      return [];
    }
  }

  async handleAddClassToTraining(trainingId: number, classId: number) {
    try {
      await this.axiosInstance.post(`/organizations/${this.orgId}/trainings/${trainingId}/classes/${classId}`, {});
      return true;
    } catch (error) {
      console.error('添加课程失败:', error);
      return false;
    }
  }

  async handleRenameTraining(trainingId: number, newName: string): Promise<boolean> {
    try {
      await this.axiosInstance.put(`/organizations/${this.orgId}/trainings/${trainingId}`, {
        name: newName
      });
      return true;
    } catch (error) {
      console.error('课程名称更新错误:', error);
      return false;
    }
  }

  async saveTrainingData(trainingId: number, data: any): Promise<boolean> {
    try {
      await this.axiosInstance.put(`/organizations/${this.orgId}/trainings/${trainingId}`, { data });
      return true;
    } catch (error) {
      console.error('课程名称更新错误:', error);
      return false;
    }
  }

  async updateTrainingStatus(trainingId: number, newStatus: number): Promise<boolean> {
    try {
      await this.axiosInstance.put(`/organizations/${this.orgId}/trainings/${trainingId}`, {
        status: newStatus
      });
      return true;
    } catch (error) {
      console.error('更新培训状态失败:', error);
      return false;
    }
  }

  async handleDeleteTraining(trainingId: number): Promise<boolean> {
    try {
      await this.axiosInstance.delete(`/organizations/${this.orgId}/trainings/${trainingId}`);
      return true;
    } catch (error) {
      console.error('删除培训失败:', error);
      return false;
    }
  }

  async handleRemoveClassFromTraining(trainingId: number, classId: number): Promise<boolean> {
    try {
      console.log('handleRemoveClassFromTraining classid is:', classId);
      await this.axiosInstance.delete(`/organizations/${this.orgId}/trainings/${trainingId}/classes/${classId}`);
      return true;
    } catch (error) {
      console.error('删除课程失败:', error);
      return false;
    }
  }

  async fetchTrainingStatus(trainingId: number): Promise<void> {
    try {
      await this.axiosInstance.get(`/organizations/${this.orgId}/trainings/${trainingId}`);
      console.log('获取培训状态成功');
    } catch (error) {
      console.error('获取培训状态失败:', error);
    }
  }

  async handleRemoveGroupFromTraining(trainingId: number, groupId: number): Promise<boolean> {
    try {
      await this.axiosInstance.delete(`/organizations/${this.orgId}/trainings/${trainingId}/groups/${groupId}`);
      return true;
    } catch (error) {
      console.error('移除学习小组失败:', error);
      return false;
    }
  }

  async getUsersByIds(userIds: number[], trainingId: number): Promise<any[]> {
    try {
      const response = await this.axiosInstance.get(
        `${this.orgServerUrl}/trainings/${trainingId}/users/${userIds.join(',')}`
      );

      return response.data.map((user: any) => {
        let userInfo = {};
        if (user.Data && typeof user.Data === 'string') {
          try {
            const parsedData = JSON.parse(user.Data);
            userInfo = JSON.parse(parsedData);
          } catch (error) {
            console.error('Error parsing user data:', error);
          }
        }
        return {
          ...userInfo,
          userId: user.userId
        };
      });
    } catch (error) {
      console.error('获取用户信息失败:', error);
      return [];
    }
  }

  async getTrainingAllUsers(trainingId: number): Promise<IStudent[]> {
    try {
      if (!this.orgServerUrl) {
        return [];
      } else {
        const response = await this.axiosInstance.get(`${this.orgServerUrl}/trainings/${trainingId}/users`);
        return response.data
          .map((user: any) => {
            try {
              const userData = JSON.parse(JSON.parse(user.Data));
              console.log('userData: ', userData);
              return {
                key: user.userId,
                realName: userData.realName || '',
                trainingGroup: '',
                displayName: userData.displayName || '',
                uniqueId: '',
                gender: userData.gender || '',
                phone: userData.phone || '',
                email: userData.email || '',
                wechatId: userData.weChat || '',
                education: userData.education || '',
                provinceCity: userData.provinceCity || '',
                yearsAsChristian: userData.yearsAsChristian || '',
                careExperience: userData.careExperience || '',
                trainingExpectation: userData.trainingExpectation || ''
              } as IStudent;
            } catch (error) {
              console.error('Error parsing user data:', error);
              return null;
            }
          })
          .filter((student: IStudent | null): student is IStudent => student !== null);
      }
    } catch (error) {
      console.error('获取所有用户信息失败:', error);
      return [];
    }
  }

  async getTrainingUser(trainingId: number): Promise<any> {
    try {
      const response = await this.axiosInstance.get(`${this.orgServerUrl}/trainings/${trainingId}/user`);

      return response.data;
    } catch (error) {
      console.error('获取用户信息失败:', error);
      throw error;
    }
  }

  async fetchStudentsForGroup(groupId: number, trainingId: number): Promise<IStudent[]> {
    try {
      const groupInfo = await this.getGroupInfo(groupId);
      if (!groupInfo || !groupInfo.users) {
        return [];
      }

      let students = [];
      if (!this.orgServerUrl) {
        //there is no org specific user data
        students = groupInfo.users.map((user: any) => {
          return {
            ...user,
            key: user.userId,
            trainingGroup: groupInfo.name,
            displayName: user.name,
            uniqueId: user.loginId
          };
        });
      } else {
        const allUsers = await this.getTrainingAllUsers(trainingId);
        students = allUsers
          .filter((user) => groupInfo.users.some((groupUser: IGroupUser) => groupUser.userId === user.key))
          .map((user) => {
            const groupUser = groupInfo.users.find((groupUser: IGroupUser) => groupUser.userId === user.key);

            return {
              ...user,
              trainingGroup: groupInfo.name,
              displayName: groupUser ? groupUser.name : '',
              uniqueId: groupUser ? groupUser.loginId : ''
            };
          });
      }
      console.log('学员信息:', students);
      return students;
    } catch (error) {
      console.error('获取小组学员信息失败:', error);
      return [];
    }
  }

  async checkUserRegistration(
    orgId: string,
    trainingId: string,
    joinToken: string
  ): Promise<{ canJoin?: boolean; error?: string }> {
    try {
      const response = await this.axiosInstance.get(
        `/organizations/${orgId}/trainings/${trainingId}/joincheck/${joinToken}`
      );

      return { canJoin: response.data.canJoin };
    } catch (error) {
      console.error('检查报名状态失败:', error);
      if (error.response?.status === 500) {
        const message = error.response.data.error || '内部服务器错误';
        if (message === 'Training is not in registration or cannot find training!') {
          return { error: '报名尚未开始或未找到该培训' };
        }
        return { error: message };
      }
      return { error: '网络错误或服务器无法访问' };
    }
  }

  async submitRegistrationData(submissionData: any, trainingId: string): Promise<boolean> {
    const userDataJsonString = JSON.stringify(submissionData);

    try {
      const response = await this.axiosInstance.put(`${this.orgServerUrl}/trainings/${trainingId}/user`, {
        Data: userDataJsonString
      });

      if (response.status === 201) {
        console.log('提交成功:', response);
        return true;
      } else {
        console.error('提交失败，响应状态码:', response.status);
        return false;
      }
    } catch (error) {
      console.error('最终提交失败:', error);
      return false;
    }
  }

  async joinTrainingGroup(trainingId: string, joinToken: string) {
    try {
      const joinResponse = await this.axiosInstance.post(
        `/organizations/${this.orgId}/trainings/${trainingId}/join/${joinToken}`,
        null
      );

      if (joinResponse.status === 201 || joinResponse.status === 200) {
        console.log('加入小组成功');
        return joinResponse.data;
      } else {
        console.error('加入小组失败，响应状态码:', joinResponse.status);
        throw new Error(`加入小组失败，响应状态码: ${joinResponse.status}`);
      }
    } catch (error) {
      console.error('请求失败:', error);
      throw error;
    }
  }

  async deleteRegistration(trainingId: string, userIds: number[]) {
    try {
      await this.axiosInstance.delete(`/trainings/${trainingId}/users/${userIds}`);
      console.log('成功删除注册信息');
      return true;
    } catch (error) {
      console.error('删除注册请求失败:', error);
      return false;
    }
  }

  async resetPassword(email: string): Promise<boolean> {
    if (!email || email.trim().length === 0) {
      console.error('Email is required for resetting password.');
      return false;
    }

    try {
      await this.axiosInstance.put('/user/resetPassword', { loginId: email.trim() });
      if (isFishMeetVariant) {
        return true;
      }
      return true;
    } catch (error) {
      if (isFishMeetVariant) {
        throw Error(error.response?.data?.error || error.message);
      }
      return false;
    }
  }

  async newPassword(newPass: string): Promise<boolean> {
    try {
      await this.axiosInstance.put('/user/profile', { password: newPass });
      return true;
    } catch (error) {
      console.error('Reset password failed:', error.response?.data || error.message);
      return false;
    }
  }

  public async signUp(displayName: string, email: string, password: string): Promise<any> {
    const url = '/user/create';
    const data = {
      displayName: displayName,
      loginId: email,
      password: password
    };

    try {
      const response = await this.axiosInstance.post(url, data);
      if (response.status === 201) {
        console.log('Signup successful:', response.data);
        localStorage.setItem('access_token', response.data.accessToken);

        return { success: true, data: response.data };
      } else {
        console.error('Signup failed with status:', response.status);
      }
    } catch (error) {
      console.error('Error during signup:', error);
      if (error.response?.status === 400) {
        // Log or alert the specific error message received from the backend
        console.error('Error during signup:', error.response.data.error);
        alert(error.response.data.error);
      }
      throw error;
    }
    return undefined;
  }

  public async signIn(loginId: string, password: string): Promise<any> {
    try {
      const response = await this.axiosInstance.post('/user/login', {
        loginId,
        password
      });

      localStorage.setItem('access_token', response.data.accessToken);
      return { success: true, data: response.data };
    } catch (error) {
      let errorMessage = '登录失败';
      if (error.response && error.response.data) {
        errorMessage = error.response.data || errorMessage;
        if (typeof errorMessage === 'object') {
          errorMessage = JSON.stringify(errorMessage);
        }
      }
      return { success: false, error: errorMessage };
    }
  }

  async deleteUser(): Promise<string> {
    if (!isUserSignedIn()) {
      return 'NOT_SIGNED_IN';
    }
    const url = '/user/delete';
    try {
      await this.axiosInstance.delete(url);
      wipeSignInState();
      return 'SUCCESS';
    } catch (error) {
      console.log(error);
    }

    return 'FAILED';
  }

  async getUsersInRole(contextId: string | number, role: string): Promise<IUser[]> {
    try {
      let apiUrl;
      switch (role) {
        case 'Author':
          apiUrl = `/bookAdmin/lesson/${contextId}/authors`;
          break;
        case 'Admin':
          apiUrl = `/organizations/${contextId}/admins`;
          break;
        default:
          return [];
      }
      const response = await this.axiosInstance.get(apiUrl);
      const ru = response.data as IUser[];
      if (role === 'Admin') {
        if (contextId !== 1) {
          return ru.filter((uu) => [3, 15, 68].indexOf(uu.userId) === -1);
        }
      }
      return ru;
    } catch (error) {
      console.log(error);
    }
    return [];
  }

  async removeSingleRoleUser(role: string, contextId: string, userId: number): Promise<boolean> {
    try {
      let apiUrl;
      switch (role) {
        case 'Author':
          apiUrl = `/bookAdmin/lesson/${contextId}/authors/${userId}`;
          break;
        case 'Admin':
          apiUrl = `/organizations/${contextId}/admins/${userId}`;
          break;
        default:
          return false;
      }
      await this.axiosInstance.delete(apiUrl);
      return true;
    } catch (error) {
      console.log(error);
    }
    return false;
  }

  async addSingleRoleUser(role: string, contextId: string, userId: number): Promise<boolean> {
    console.log('addSingleRoleUser: ', role, contextId, userId);
    try {
      let apiUrl;
      switch (role) {
        case 'Author':
          apiUrl = `/bookAdmin/lesson/${contextId}/authors/${userId}`;
          break;
        case 'Admin':
          apiUrl = `/organizations/${contextId}/admins/${userId}/1`;
          break;
        default:
          return false;
      }
      const response = await this.axiosInstance.post(apiUrl);
      console.log('addSingleRoleUser 2: ', response);
      if (response.status === 201) {
        return true;
      }
    } catch (error) {
      console.log(error);
    }
    return false;
  }

  // this API should *ONLY* be called from phoneview, that offers
  // class json files to have API calls
  async callApi(url: string) {
    try {
      const response = await this.axiosInstance.get(url);
      return response;
      /*
      if (response.status === 200) {
        return response.data;
      }
      throw new Error(`response is not 200: ${response.status}`);
      */
    } catch (error: any) {
      console.error('network call error: ', error);
      return { status: 601 };
    }
  }

  async launchUrl(launchUrl: string) {
    try {
      const response = await this.axiosInstance.post('/user/launchUrl', { launchUrl });
      return response.data;
    } catch (error: any) {
      console.error('network call error: ', error);
      return null;
    }
  }
  // 频道
  async getChannels() {
    console.log('getChannels', '/channels');
    try {
      const response = await this.axiosInstance.get('/channels');
      return response.data;
    } catch (error) {
      console.error('获取频道列表失败:', error);
      return null;
    }
  }

  async getOwnedChannels() {
    try {
      const response = await this.axiosInstance.get('/channels/owned');
      return response.data;
    } catch (error) {
      console.error('获取拥有的频道失败:', error);
      return null;
    }
  }

  async createChannel(data: IChannelCreate) {
    try {
      const response = await this.axiosInstance.post('/channels', {
        ...data,
        organizationId: this.orgId
      });
      return response.data;
    } catch (error) {
      console.error('创建频道失败:', error);
      return null;
    }
  }

  async updateChannel(channelId: number, data: any) {
    try {
      const response = await this.axiosInstance.put(`/channels/${channelId}`, data);
      return response.data;
    } catch (error) {
      console.error('更新频道失败:', error);
      return null;
    }
  }

  async deleteChannel(channelId: number) {
    try {
      const response = await this.axiosInstance.delete(`/channels/${channelId}`);
      return response.data;
    } catch (error) {
      console.error('删除频道失败:', error);
      return null;
    }
  }

  async uploadChannelFile(
    channelId: number,
    file: File,
    coverImage?: boolean,
    callback?: (v: number) => void,
    programId?: number
  ) {
    try {
      const formData = new FormData();
      formData.append('file', file);
      let url = `/channels/${channelId}/uploadFile`;
      if (coverImage) {
        url += '?coverImage=1';
      } else if (programId) {
        url += `?programId=${programId}`;
      }
      const response = await this.axiosInstance.post(url, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent: any) => {
          const progress = progressEvent.loaded / progressEvent.total;
          callback?.(progress);
        }
      });
      return response.data;
    } catch (error) {
      console.error('上传频道文件失败:', error);
      return null;
    }
  }

  async deleteChannelProgramAsync(channelId: number, programId: number) {
    try {
      await this.axiosInstance.delete(`/channels/${channelId}/programs/${programId}`);
      return true;
    } catch (error) {
      console.error('删除program文件失败:', error);
      return false;
    }
  }
  async createChannelProgramAsync(channelId: number, title: string) {
    try {
      const response = await this.axiosInstance.post(`/channels/${channelId}/programs`, {
        title
      });
      return response.data;
    } catch (error) {
      console.error('创建program失败:', error);
      return null;
    }
  }
  async updateChannelProgramAsync(channelId: number, programId: number, program: IProgram) {
    try {
      const response = await this.axiosInstance.put(`/channels/${channelId}/programs/${programId}`, program);
      return response.data;
    } catch (error) {
      console.error('更新program失败:', error);
      return null;
    }
  }
  async getChannelAuthors(channelId: number) {
    try {
      const response = await this.axiosInstance.get(`/channels/${channelId}/authors`);
      return response.data;
    } catch (error) {
      console.error('获取频道作者失败:', error);
      return null;
    }
  }
  async getChannel(channelId: number) {
    try {
      const response = await this.axiosInstance.get(`/channels/${channelId}`);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch channel:', error);
    }
    return null;
  }

  async addChannelAuthor(channelId: number, userId: number) {
    try {
      const response = await this.axiosInstance.put(`/channels/${channelId}/authors/${userId}`);
      return response.data;
    } catch (error) {
      console.error('添加频道作者失败:', error);
      return null;
    }
  }

  async deleteChannelAuthor(channelId: number, userId: number) {
    try {
      const response = await this.axiosInstance.delete(`/channels/${channelId}/authors/${userId}`);
      return response.data;
    } catch (error) {
      console.error('删除频道作者失败:', error);
      return null;
    }
  }

  async getCover(channelId: number) {
    try {
      const response = await this.axiosInstance.get(`/channels/${channelId}/cover`);
      return response.data;
    } catch (error) {
      console.error('获取频道封面失败:', error);
      return { error };
    }
  }
  async saveJsonFileAsync({ channelId, filename, data }: IChannelJsonSave) {
    try {
      const response = await this.axiosInstance.post(`/channels/${channelId}/save/${filename}`, data);
      return response.data;
    } catch (error) {
      console.error('保存json失败:', error);
      return null;
    }
  }
  async getJsonFileAsync(channelId: number, filename: string) {
    try {
      const response = await this.axiosInstance.get(`/channels/${channelId}/content/${filename}`);
      return response.data;
    } catch (error) {
      console.error('保存json失败:', error);
      return {};
    }
  }
  async getChannelPrograms(channelId: number, nextToken?: string) {
    try {
      const response = await this.axiosInstance.get(
        `/channels/programs?channelId=${channelId}&pageSize=${10}&nextToken=${nextToken}`
      );
      return response.data;
    } catch (error) {
      console.error('保存json失败:', error);
      return {};
    }
  }
  async getChannelProgramDetail(programId: number) {
    try {
      const response = await this.axiosInstance.get(`/channels/programs/${programId}`);
      return response.data;
    } catch (error) {
      console.error('保存json失败:', error);
      return {};
    }
  }

  public async putChannelSubscribe(channelId: number) {
    try {
      const response = await this.axiosInstance.put(`/channels/${channelId}/subscribe`);
      return response.data;
    } catch (error) {
      console.error('Failed to subscribe channel:', error);
    }
    return null;
  }

  public async putChannelUnsubscribe(channelId: number) {
    try {
      const response = await this.axiosInstance.put(`/channels/${channelId}/unsubscribe`);
      return response.data;
    } catch (error) {
      console.error('Failed to unsubscribe channel:', error);
    }
    return null;
  }

  public async putProgramViews(programId: string) {
    try {
      const response = await this.axiosInstance.put(`/channels/programs/${programId}/views`);
      return response.data;
    } catch (error) {
      console.error('Failed to update program views:', error);
    }
    return null;
  }
}

export const _dataProvider = new DataProvider('');
